import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Link,
  Stack,
  Typography
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import "./styles/applicationDetail.css";
import {
  EmployeePersonalInfoConfig,
  EmployeeAddressInfoConfig,
  EmployeeWorkInfoConfig,
  AdditionalEmployeeInfoConfig
} from "./config/employeeDetailConfig";
import WithAuth from "../../Hoc/WithAuth";
import {
  Employee,
  GetEmployeeInfoResponse
} from "../../Graphql/types/employee";
import { getEmployeeByIdQuery } from "../../Graphql/querries/employeeQuery";
import EditButton from "../Common/EditButton";
import { EDIT_EMPLOYEE } from "../Routes";

const EmployeeDetail = () => {
  const navigate = useNavigate();
  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  // Extract the 'id' from the URL parameters
  const { employeeId } = useParams<{ employeeId: string }>();
  const { setAlert } = useContext(AlertBannerContext);
  const [employeeDetail, setEmployeeDetail] = useState<Employee | undefined>();

  const { mutate, isPending } = useMutation<
    GetEmployeeInfoResponse,
    Error,
    string
  >({
    mutationKey: ["getEmployeeDetail"],
    mutationFn: (id) => fetchGraphQL(getEmployeeByIdQuery, { id }, setAlert),
    onSuccess: (response) => {
      const data = response.getEmployeeById;
      setEmployeeDetail(data);
    }
  });

  const getValueByKey = (
    obj: Employee,
    key: string
  ): string | boolean | number | undefined => {
    return key.split(".").reduce<unknown>((acc, part) => {
      if (acc && typeof acc === "object" && part in acc) {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, obj as unknown) as string | boolean | number | undefined;
  };

  useEffect(() => {
    if (employeeId) mutate(employeeId);
  }, [employeeId]);

  const onEditEmployee = () => {
    navigate(`${EDIT_EMPLOYEE}/${employeeId}`);
  };

  if (isPending) {
    return (
      <Stack width="100%" paddingBottom="1em" alignSelf="center">
        <LinearProgress />
      </Stack>
    );
  }

  if (!employeeDetail) {
    return <Typography>Employee doesn&apos;t exist</Typography>;
  }

  return (
    <section style={containerStyle}>
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Employee Detail
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          startIcon={<ArrowBackIcon />}
        >
          back
        </Button>
        <EditButton onClick={onEditEmployee} />
      </Stack>
      <Stack
        style={{
          background: "white",
          paddingBottom: "2em",
          gap: "1em",
          display: "flex",
          width: "100%",
          boxSizing: "border-box",
          flexDirection: "column"
        }}
      >
        <Stack
          display="flex"
          flexDirection="column"
          width="100%"
          gap={3}
          fontWeight="bold"
          padding="2em"
          boxSizing="border-box"
        >
          <Typography variant="h6">Personal Info</Typography>
          <Box
            sx={{
              width: "100%",
              height: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap"
            }}
          >
            {EmployeePersonalInfoConfig.map((config) => (
              <Stack
                key={config.key}
                style={{
                  border: "solid 0.5px grey",
                  padding: "1em",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1em",
                  width: "50%"
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {`${config.displayTitle}: `}
                </Typography>
                <Typography variant="body2">
                  {getValueByKey(employeeDetail, config.key) || "N/A"}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Stack>

        <Stack
          display="flex"
          flexDirection="column"
          width="100%"
          gap={3}
          fontWeight="bold"
          padding="2em"
          boxSizing="border-box"
        >
          <Typography variant="h6">Address Info</Typography>
          <Box
            sx={{
              width: "100%",
              height: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap"
            }}
          >
            {EmployeeAddressInfoConfig.map((config) => (
              <Stack
                key={config.key}
                style={{
                  border: "solid 0.5px grey",
                  padding: "1em",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1em",
                  width: "50%"
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {`${config.displayTitle}: `}
                </Typography>
                <Typography variant="body2">
                  {getValueByKey(employeeDetail, config.key) || "N/A"}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Stack>

        <Stack
          display="flex"
          flexDirection="column"
          width="100%"
          gap={3}
          fontWeight="bold"
          padding="2em"
          boxSizing="border-box"
        >
          <Typography variant="h6">Work Info</Typography>
          <Box
            sx={{
              width: "100%",
              height: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap"
            }}
          >
            {EmployeeWorkInfoConfig.map((config) => (
              <Stack
                key={config.key}
                style={{
                  border: "solid 0.5px grey",
                  padding: "1em",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1em",
                  width: "50%"
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {`${config.displayTitle}: `}
                </Typography>
                <Typography variant="body2">
                  {typeof getValueByKey(employeeDetail, config.key) ===
                  "boolean" ? (
                    <div>
                      {getValueByKey(employeeDetail, config.key)
                        ? "True"
                        : "False"}
                    </div>
                  ) : (
                    getValueByKey(employeeDetail, config.key) || "N/A"
                  )}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Stack>

        <Stack
          display="flex"
          flexDirection="column"
          width="100%"
          gap={3}
          fontWeight="bold"
          padding="2em"
          boxSizing="border-box"
        >
          <Typography variant="h6">Additional Info</Typography>
          <Box
            sx={{
              width: "100%",
              height: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap"
            }}
          >
            {AdditionalEmployeeInfoConfig.map((config) => (
              <Stack
                key={config.key}
                style={{
                  border: "solid 0.5px grey",
                  padding: "1em",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1em",
                  width: "50%"
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {`${config.displayTitle}: `}
                </Typography>
                <Typography variant="body2">
                  {config.key?.includes("Url") ? (
                    <Link
                      href={`${getValueByKey(employeeDetail, config.key) || "N/A"}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ) : (
                    getValueByKey(employeeDetail, config.key) || "N/A"
                  )}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Stack>
      </Stack>
    </section>
  );
};

export default WithAuth(EmployeeDetail);
